import { useQuery } from '@tanstack/react-query'
import { Select, Input } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ViewIcon from '@assets/icons/viewSvg'
import Header from '@components/header'
import Layout from '@components/Layout'
import ViewSalesModal from '@components/Modal/ViewSalesWithDocEntryModal'
import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchGoodIssueReport, fetchDepartments, fetchSalesReportByDepartment , fetchWarehouseSelect, fetchItemsGroups } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const SalesReportsByDepartment = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [department, setDepartment] = useState('')
	const [itemGroup, setItemGroup] = useState('')
	const [itemName, setItemName] = useState('')
	const [price, setPrice] = useState('')

	const debouncedItemName = useDebounce(itemName, 500)
	const debouncedPrice = useDebounce(price, 500)

	const [pageSize] = useState(20)
	const [page, setPage] = useState(0)
	const [loadingExcel, setLoadingExcel] = useState(false)
	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'sales-report-by-department',
			pageSize,
			page,
			startDate,
			endDate,
			department,
			itemGroup,
			debouncedItemName,
			debouncedPrice,
		],
		queryFn: fetchSalesReportByDepartment,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: departmentData = [] } = useQuery({
		queryKey: ['departments'],
		queryFn: fetchDepartments,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const { data: itemsGroupData = [] } = useQuery({
		queryKey: ['itemsGrp'],
		queryFn: fetchItemsGroups,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const baseColumns = [
		{
			title: t('department'),
			dataIndex: 'department',
			key: 'department',
		},
		{
			title: t('itemGroup'),
			dataIndex: 'itemGroup',
			key: 'itemGroup',
		},
		{
			title: t('itemName'),
			dataIndex: 'itemName',
			key: 'itemName',
		},
		{
			title: t('quantity'),
			dataIndex: 'quantity',
			key: 'quantity',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('price'),
			dataIndex: 'price',
			key: 'price',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('total'),
			dataIndex: 'total',
			key: 'total',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
	]

	const columns = [...baseColumns]

	const handleNextPage = () => {
		if (data.length === pageSize) {
			setPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prevPage) => prevPage - 1)
		}
	}

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/sales-report-by-department/export',
			params: {
				startDate,
				endDate,
				department,
				itemGroup,
				itemName: debouncedItemName,
				price: debouncedPrice,
				page: 0,
				pageSize: 1000,
			},
			fileName: t('salesReportByDepartment'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'salesReportByDepartment'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				hasMoreData={data.length === pageSize}
				currentPage={page + 1}
				handlePreviousPage={handlePreviousPage}
				handleNextPage={handleNextPage}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				renderFilters={() => {
					return (
						<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4">
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('startDate')}</p>
								<Input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									placeholder={t('startDate')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('endDate')}</p>
								<Input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									placeholder={t('endDate')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('department')}</p>
								<Select
									value={department}
									className="flex h-8 w-full items-center justify-center "
									style={{ color: 'black' }}
									onSelect={(val) => {
										setDepartment(val)
									}}
									options={departmentData.map((d) => {
										return {
											label: d.name,
											value: d.name,
										}
									})}
									filterOption={(input, option) =>
										option.label
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									showSearch
									allowClear
									onClear={() => {
										setDepartment('')
									}}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('itemGroup')}</p>
								<Select
									value={itemGroup}
									className="flex h-8 w-full items-center justify-center "
									style={{ color: 'black' }}
									onSelect={(val) => {
										setItemGroup(val)
									}}
									options={itemsGroupData.map((group) => {
										return {
											label: group.itmsGrpNam,
											value: group.itmsGrpCod,
										}
									})}
									filterOption={(input, option) =>
										option.label
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									showSearch
									allowClear
									onClear={() => {
										setItemGroup('')
									}}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('itemName')}</p>
								<Input
									value={itemName}
									onChange={(e) => setItemName(e.target.value)}
									placeholder={t('itemName')}
									allowClear
								/>
							</div>

						</div>
					)
				}}
			/>

		</Layout>
	)
}

export default SalesReportsByDepartment
